import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { useAppSelector } from "../redux-hooks";
import { createAsyncThunk } from '@reduxjs/toolkit';
import { GeneralV3Response, LoadingType } from '../../../types/CommonTypes';
import { updateElementValue } from '../../../utils/updateElementValue';
import { ManufacturerCreateObj } from '../../../components/operation/manufacturerNotification/CreateManufacturer';
import { editManufacturer, getDetailsOfManufacturerById, getManufacturerByRoleForCommission, getManufacturerList, getManufacturerListByPracticeIds, getManufacturerListByRole, saveManufacturer } from "../../../service/ManufacturerService";
import { ManufacturerInfo } from '../commonSlice';

export type ManufactureInfo={
  contactPhone:string;
  customConfig:{
    invoice_due_date?:string;
    invoice_net_days?:string;
    //CS-1518 START
    cc_authorized_practice_user?:string;
    provider_signature_required?:string;
    purchase_and_bill?:string;
    template_level?:string;
    [key: string]: any; 
    //CS-1518 END
  };
  manufacturerId:number;
  manufacturerName:string;
  cartOption: string;
}

export type SearchManufacturerRequest = {
  manufacturerName?: string;
  contactPhone: string;
  manufacturerStatus: string[];
  mfgIds?: number[];
  currentPage?: number;
  itemsPerPage?: number;
  downloadState?: boolean; // CS-1685
  activeOnly?: boolean;
  isPharmacy?: boolean | null;
  sortBy?: "manufacturerName" | "inactiveDate" | "legalName";
  sortOrder?: "asc" | "desc";
}

export interface ManufacturerData {
  manufacturerName:string;
  contactPhone:string;
  notificationEmail:string;
  customConfig:string;
  manufacturerId:string;
}

export type PaginationProductList = {
  content: ManufactureInfo[];
  totalElements: number;
  totalPages: number;
  size: number;
}

type ManufacturerListState = {
    ManufacturerListInfo?: PaginationProductList | null;
    ManufacturerListFetchStatus?: LoadingType;
    manufacturerListByPractice: ManufactureInfo[] | null;
    status?: LoadingType;
    ManufacturerData?:ManufacturerCreateObj;
    //CS-1625 - Fetching manufacturer list by role
     manufacturerInfoListByUserRole: ManufacturerInfo[],
     manufacturerInfoListForCommission: ManufacturerInfo[], // CS-1765
}


const initialState: ManufacturerListState = {
    ManufacturerListInfo: undefined,
    ManufacturerData: undefined,
    manufacturerListByPractice: null,
    //CS-1625 - Fetching manufacturer list by role
    manufacturerInfoListByUserRole: [],
    manufacturerInfoListForCommission: [], // CS-1765
};

export const fetchManufacturerList = createAsyncThunk("fetchManufacturerList", async (data: SearchManufacturerRequest) => {
    const response = await getManufacturerList(data);
    const v3Response = response.data;
    console.log("V3 Response", v3Response);
    return v3Response;
});

export const saveManufacturers = createAsyncThunk("saveManufacturer", async (data: ManufacturerCreateObj, { rejectWithValue }) => {
  try {
    const response = await saveManufacturer(data); 
    return response.data;
  } catch (error) {
    //@ts-ignore
    return rejectWithValue(error?.data);
  }
});
export const editManufacturerData = createAsyncThunk("editManufacturerData", async (data: ManufacturerCreateObj, { rejectWithValue }) => {
  try {
    const response = await editManufacturer(data); 
    return response.data;
  } catch (error) {
    //@ts-ignore
    return rejectWithValue(error?.data);
  }
});

export const fetchManufacturerDataById = createAsyncThunk('fetchManufacturerDataById',async(mfrId:number)=>{
  const response = await getDetailsOfManufacturerById(mfrId);
  return response;

});

export const fetchManufacturerListByPracticeIds = createAsyncThunk('fetchManufacturerListByPracticeIds',async(practiceIds:number[])=>{
    try {
    const response = await getManufacturerListByPracticeIds(practiceIds);
    return response;
  } catch (error) {
    //@ts-ignore
    return rejectWithValue(error?.data);
  };
});

//CS-1625 - Fetching manufacturer list by role
export const fetchManufacturerListByRole = createAsyncThunk<any, { skipLoader?: boolean }>("fetchManufacturerListByRole", async ({ skipLoader = false }) => {
  const response = await getManufacturerListByRole({ 'x-skip-loader': skipLoader.toString() });
  const v3Response = response.data;
  return v3Response;
});

// CS-1765 - Fetch manufacturer list for commission using dedicated endpoint
export const fetchManufacturerListForCommission = createAsyncThunk<any, { skipLoader?: boolean }>("fetchManufacturerListForCommission", async ({ skipLoader = false }) => {
  const response = await getManufacturerByRoleForCommission({ 'x-skip-loader': skipLoader.toString() });
  const v3Response = response.data;
  return v3Response;
});

// Create the slice
const manufacturerSlice = createSlice({
  name: 'manufacturer',
  initialState,
  reducers: {
    updateElementInManufacturerSlice : updateElementValue,
    resetManufacturerData: (state: ManufacturerListState) => {
      return {
        ...state,
        ...initialState,
      };
    },
    resetManufacturerList: (state: ManufacturerListState) => {
      state.ManufacturerListInfo = undefined;
      state.ManufacturerListFetchStatus = "idle";
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchManufacturerList.pending, (state) => {
        state.ManufacturerListFetchStatus = "loading";
      })
      .addCase(fetchManufacturerList.fulfilled, (state, action: PayloadAction<GeneralV3Response>) => {
        state.ManufacturerListFetchStatus = "success";
        // state.ManufacturerListInfo = action.payload.data.searchManufacturerData; // CS-1685
        state.ManufacturerListInfo = action.payload?.data?.searchManufacturerData; // CS-1685 // CS-1984
      })
      .addCase(fetchManufacturerList.rejected, (state, action) => {
        state.ManufacturerListFetchStatus = 'error';
      })
      .addCase(fetchManufacturerDataById.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchManufacturerDataById.fulfilled, (state, action: PayloadAction<GeneralV3Response>) => {
        state.status= "success";
        // state.ManufacturerData = action.payload.data.data;
        state.ManufacturerData = action.payload?.data?.data; // CS-1984
      })
      .addCase(fetchManufacturerDataById.rejected, (state, action) => {
        state.status = 'error';
      })
      .addCase(fetchManufacturerListByPracticeIds.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchManufacturerListByPracticeIds.fulfilled, (state, action: PayloadAction<GeneralV3Response>) => {
        state.status = "success";
        // state.manufacturerListByPractice = action.payload.data.data;
        state.manufacturerListByPractice = action.payload?.data?.data; // CS-1984
      })
      .addCase(fetchManufacturerListByPracticeIds.rejected, (state, action) => {
        state.status = 'error';
      })
      //CS-1625 - Fetching manufacturer list by role
      .addCase(fetchManufacturerListByRole.pending, (state) => {
        state.status= "loading";
      })
      .addCase(fetchManufacturerListByRole.fulfilled, (state, action) => {
        state.status= "success";
        state.manufacturerInfoListByUserRole = action.payload?.data;
      })
      .addCase(fetchManufacturerListByRole.rejected, (state, action) => {
        state.status = 'error';
      })
      // CS-1765 - Fetching manufacturer list for commission using dedicated endpoint
      .addCase(fetchManufacturerListForCommission.pending, (state) => {
        state.status= "loading";
      })
      .addCase(fetchManufacturerListForCommission.fulfilled, (state, action) => {
        state.status= "success";
        state.manufacturerInfoListForCommission = action.payload?.data;
      })
      .addCase(fetchManufacturerListForCommission.rejected, (state, action) => {
        state.status = 'error';
      })
  }
});

// Export actions for use in components
export const {
    resetManufacturerData,
    resetManufacturerList,
} = manufacturerSlice.actions;

// Export the reducer for the store configuration
export const useManufacturerListSlice = () => useAppSelector((state) => state.manufacturerSlice);
export default manufacturerSlice.reducer;
